import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, FilePlus2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public amendment-signing page used by the original convention signer when
 * an admin needs to modify a contractual field after the convention was signed.
 * No authentication required — token-based.
 */
export default function AmendmentSignPublic() {
  const { token } = useParams();
  const [amd, setAmd] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/amendments/${token}`)
      .then(({ data }) => {
        setAmd(data);
        if (data.status === 'applied' || data.signed_at) setSigned(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const onSign = async (dataUrl, name) => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/amendments/${token}/sign`, {
        signature_b64: dataUrl,
        signer_name: name,
      });
      setSigned(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la signature');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center p-6">
        <div className="soft-card p-8 max-w-md text-center" data-testid="amendment-sign-error">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
          <h1 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Lien non valide</h1>
          <p className="text-[13px] text-[#6B6C85]">{error}</p>
        </div>
      </div>
    );
  }

  const primary = amd?.branding?.primary || '#4F46E5';
  const orgName = amd?.branding?.org_name || 'OFManager';

  return (
    <div className="min-h-screen bg-[#FAFAFE] py-10 px-4" data-testid="amendment-sign-page">
      <div className="max-w-3xl mx-auto">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-5">
          {amd?.branding?.logo_b64 ? (
            <img src={amd.branding.logo_b64} alt={orgName} className="h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: primary }}>
              {orgName.slice(0, 1)}
            </div>
          )}
          <div className="text-[14px] font-bold text-[#1A1B2E]">{orgName}</div>
        </div>

        <div className="soft-card p-8 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#EEF2FF', color: primary }}>
              <FilePlus2 className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-wide text-[#6B6C85] font-semibold">Avenant à la convention</div>
              <h1 className="text-[20px] font-bold text-[#1A1B2E]">{amd.session?.title || amd.session?.program || 'Formation'}</h1>
            </div>
          </div>

          <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-4 mb-5">
            <div className="text-[11.5px] uppercase tracking-wide text-[#6B6C85] font-semibold mb-1">Motif de la modification</div>
            <p className="text-[13.5px] text-[#1A1B2E] whitespace-pre-line">{amd.reason}</p>
          </div>

          <div className="text-[11.5px] uppercase tracking-wide text-[#6B6C85] font-semibold mb-2">Modifications apportées</div>
          <div className="space-y-2">
            {(amd.changes || []).map((c, i) => (
              <div key={i} className="rounded-xl border border-[#EFF0F6] p-3">
                <div className="text-[12px] font-semibold text-[#1A1B2E] mb-1.5">{c.label || c.field}</div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-center text-[12.5px]">
                  <div className="rounded-lg bg-[#FEF2F2] border border-[#FECACA] px-3 py-2">
                    <div className="text-[10.5px] uppercase tracking-wide text-[#B91C1C] font-semibold mb-0.5">Avant</div>
                    <div className="text-[#1A1B2E] line-through opacity-70">{c.before || '—'}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#8E8FA5] mx-auto rotate-90 md:rotate-0" />
                  <div className="rounded-lg bg-[#E6FBF7] border border-[#CFF5EC] px-3 py-2">
                    <div className="text-[10.5px] uppercase tracking-wide text-[#0F766E] font-semibold mb-0.5">Après</div>
                    <div className="text-[#0F766E] font-semibold">{c.after || '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {signed ? (
          <div className="soft-card p-8 text-center" data-testid="amendment-sign-success">
            <CheckCircle2 className="w-12 h-12 text-[#0F766E] mx-auto mb-3" />
            <h2 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Avenant signé ✓</h2>
            <p className="text-[13px] text-[#6B6C85]">
              {amd.signed_at ? <>Signé le {new Date(amd.signed_at).toLocaleString('fr-FR')}.</> : 'Votre signature a bien été enregistrée.'}
              <br />Les modifications sont désormais appliquées à votre convention.
            </p>
          </div>
        ) : (
          <div className="soft-card p-8" data-testid="amendment-sign-pad-wrap">
            <h2 className="text-[16px] font-bold text-[#1A1B2E] mb-1">Signer l'avenant</h2>
            <p className="text-[12px] text-[#6B6C85] mb-4">
              En signant ci-dessous, vous acceptez les modifications listées ci-dessus.
              Cet avenant complète la convention de formation initialement signée.
            </p>
            <SignaturePad initialName={amd.expected_signer || ''} onSave={onSign} onCancel={() => {}} />
            {submitting && <div className="mt-2 text-[12px] text-[#4F46E5] flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</div>}
          </div>
        )}
      </div>
    </div>
  );
}
